import NavIconBase, { type NavIconProps } from './NavIconBase';

/**
 * Bespoke icon set for the primary sidebar, transposed from the Claude Design
 * mockup (`components/Sidebar.dc.html`). Kept as one family file on purpose:
 * the icons are a single design set that shares its entire `<svg>` shell
 * (see `NavIconBase`), so splitting them across 18 files would duplicate the
 * wrapper 18 times and hide the fact that they only differ by geometry.
 *
 * Scope note: these replace iconoir **in the sidebar only**. The rest of the
 * app stays on iconoir — this is not a global icon migration.
 */

export const DashboardIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M3.2 10.9 12 3.9l8.8 7V19a1.5 1.5 0 0 1-1.5 1.5H4.7A1.5 1.5 0 0 1 3.2 19Z" />
    <path d="M9.4 20.5v-5h5.2v5" />
  </NavIconBase>
);

export const InboxIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M21 11.6c0 4.5-4 8.1-9 8.1a10.3 10.3 0 0 1-3.1-.47L3.9 21l1.35-3.7A7.7 7.7 0 0 1 3 11.6C3 7.1 7 3.5 12 3.5s9 3.6 9 8.1Z" />
  </NavIconBase>
);

/** Person with a plus: who applied to the brand's briefs and programs. */
export const ApplicationsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={9.8} cy={8} r={3.5} />
    <path d="M3.4 20.6v-1a5 5 0 0 1 5-5h2.8a5 5 0 0 1 5 5v1" />
    <path d="M19 7.6v6" />
    <path d="M16 10.6h6" />
  </NavIconBase>
);

export const CollabsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={9.8} cy={8} r={3.5} />
    <path d="M3.4 20.6v-1a5 5 0 0 1 5-5h2.8a5 5 0 0 1 5 5v1" />
    <path d="m18.5 3.2 1.1 2.25 2.5.36-1.8 1.76.42 2.48-2.22-1.17-2.22 1.17.42-2.48-1.8-1.76 2.5-.36Z" />
  </NavIconBase>
);

/**
 * Chain link. The mockup also declares an unused three-linked-circles
 * `affiliation` variant; only this one is ever mounted, so only this one exists.
 */
export const AffiliationLinkIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M10.4 13.6a4 4 0 0 0 5.66 0l3.4-3.4a4 4 0 0 0-5.66-5.66l-1.2 1.2" />
    <path d="M13.6 10.4a4 4 0 0 0-5.66 0l-3.4 3.4a4 4 0 0 0 5.66 5.66l1.2-1.2" />
  </NavIconBase>
);

export const CommissionsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <rect x={3.2} y={6.2} width={17.6} height={11.6} rx={3} />
    <path d="M3.2 10.6h17.6" />
    <path d="M16.2 14.4h2.6" />
  </NavIconBase>
);

export const FlowsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M14.2 2.8 6.2 13.6h5l-1.1 7.8 8-10.6h-5Z" />
  </NavIconBase>
);

/** Ribboned box. Drawn here rather than borrowed from iconoir, whose gift
 * glyph carries a heavier stroke and reads louder than its neighbours. */
export const GiftIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <rect x={3.2} y={7.2} width={17.6} height={4} rx={1.4} />
    <path d="M5 11.2v8.2a1.6 1.6 0 0 0 1.6 1.6h10.8a1.6 1.6 0 0 0 1.6-1.6v-8.2" />
    <path d="M12 7.2v13.8" />
    <path d="M12 7.2C11 4.6 9.9 3.2 8.5 3.2a2 2 0 0 0 0 4m3.5 0c1-2.6 2.1-4 3.5-4a2 2 0 0 1 0 4" />
  </NavIconBase>
);

export const CreatorsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={10.8} cy={10.8} r={7.6} />
    <path d="m16.4 16.6 4.6 4.6" />
  </NavIconBase>
);

export const MentionsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M3 10.4v3.2a1.9 1.9 0 0 0 1.45 1.84L20 19.6V4.4L4.45 8.56A1.9 1.9 0 0 0 3 10.4Z" />
    <path d="M8.6 16.7v2.7a1.9 1.9 0 0 0 3.8 0v-2.1" />
  </NavIconBase>
);

/** Globe with meridians — the Brand Space destination. */
export const BrandSpaceIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={12} cy={12} r={9} />
    <path d="M3 9.4h18" />
    <path d="M3 14.6h18" />
    <path d="M12 3a16 16 0 0 1 0 18 16 16 0 0 1 0-18Z" />
  </NavIconBase>
);

export const SettingsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M10.1 2.9h3.8l.5 2.55 2.2 1.27 2.42-.88 1.9 3.29-1.92 1.72v2.54l1.92 1.72-1.9 3.29-2.42-.88-2.2 1.27-.5 2.55h-3.8l-.5-2.55-2.2-1.27-2.42.88-1.9-3.29 1.92-1.72v-2.54L3.28 9.13l1.9-3.29 2.42.88 2.2-1.27Z" />
    <circle cx={12} cy={12} r={3} />
  </NavIconBase>
);

export const HelpIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={12} cy={12} r={9} />
    <path d="M9.3 9.4a2.7 2.7 0 1 1 3.75 2.55c-.68.34-1.15 1.02-1.15 1.8v.4" />
    <path d="M12 17.8h.01" />
  </NavIconBase>
);

export const ProfileIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={12} cy={7.9} r={3.9} />
    <path d="M4.8 20.6v-1.1a5 5 0 0 1 5-5h4.4a5 5 0 0 1 5 5v1.1" />
  </NavIconBase>
);

export const NotificationsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M6 10.2a6 6 0 0 1 12 0c0 4.7 2 6.9 2 6.9H4s2-2.2 2-6.9Z" />
    <path d="M9.9 20a2.2 2.2 0 0 0 4.2 0" />
  </NavIconBase>
);

export const SecurityIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <rect x={3.6} y={10.2} width={16.8} height={10.6} rx={3} />
    <path d="M7.6 10.2V7.8a4.4 4.4 0 0 1 8.8 0v2.4" />
  </NavIconBase>
);

export const TemplatesIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <path d="M5.4 4.4A1.6 1.6 0 0 1 7 2.8h5.9l5.7 5.7V19.6A1.6 1.6 0 0 1 17 21.2H7a1.6 1.6 0 0 1-1.6-1.6Z" />
    <path d="M12.9 2.8v4.1a1.6 1.6 0 0 0 1.6 1.6h4.1" />
    <path d="M8.8 13.4h6" />
    <path d="M8.8 16.8h6" />
  </NavIconBase>
);

export const IntegrationsIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <rect x={3} y={3} width={8} height={8} rx={2.2} />
    <rect x={13} y={3} width={8} height={8} rx={2.2} />
    <rect x={3} y={13} width={8} height={8} rx={2.2} />
    <path d="M17 13.8v6.4" />
    <path d="M13.8 17h6.4" />
  </NavIconBase>
);

export const TeamIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <circle cx={9.4} cy={8} r={3.8} />
    <path d="M2.6 20.6v-1.1a4.9 4.9 0 0 1 4.9-4.9h4a4.9 4.9 0 0 1 4.9 4.9v1.1" />
    <path d="M16.6 4.5a3.8 3.8 0 0 1 0 7" />
    <path d="M18 14.8a4.9 4.9 0 0 1 3.4 4.7v1.1" />
  </NavIconBase>
);

export const BillingIcon = (props: NavIconProps) => (
  <NavIconBase {...props}>
    <rect x={2.8} y={5.4} width={18.4} height={13.2} rx={3} />
    <path d="M2.8 10.4h18.4" />
    <path d="M6.4 14.6h3.6" />
  </NavIconBase>
);

/**
 * The mockup aliases these two rather than drawing new geometry:
 * account Preferences reuses the globe, workspace General reuses the cog.
 */
export const PreferencesIcon = BrandSpaceIcon;
export const GeneralSettingsIcon = SettingsIcon;
export const InvoicesIcon = TemplatesIcon;

export {
  default as NavIconBase,
  NAV_ICON_SIZE,
  NAV_ICON_STROKE_WIDTH,
  navIconNode,
  type NavIconProps,
} from './NavIconBase';
